/**
 * Chế độ bảo trì — bật/tắt trên trang admin (migration v118), không còn build lại FE.
 *
 * 🔴 **Trước đây là biến BUILD-TIME `VITE_MAINTENANCE_MODE`**, nên muốn treo bảng
 * "đang bảo trì" phải build + deploy lại cả frontend — đúng lúc hệ thống đang hỏng
 * và người trực cần nhất một cái công tắc. Nay admin bấm ở `/admin/maintenance`,
 * backend trả 503 cho API công khai, và FE đọc trạng thái qua `GET /v1/maintenance`.
 * Biến môi trường CHỈ CÒN LÀ ĐƯỜNG ĐÈ (xem `envForced`).
 *
 * 🔴 **CỐ Ý KHÔNG CACHE VÀO `localStorage`**, khác hẳn `event-config`. Ở đó cache
 * chống nháy giao diện và giá trị cũ thì vô hại. Ở đây một giá trị cũ là màn hình
 * bảo trì hiện ra cho người dùng của một hệ thống đang chạy hoàn toàn bình thường —
 * và họ không có cách nào bấm qua nó. Thà nháy một khung hình còn hơn khoá nhầm.
 *
 * ⚠️ **Hai đường phát hiện, cả hai đều cần.** (1) Hỏi thăm định kỳ: bảo trì bắt đầu
 * lúc người dùng đang mở sẵn trang và không bấm gì cả. (2) Bắt 503 từ chính lượt gọi
 * API (`noteMaintenanceResponse` trong `unwrap`): nhanh hơn hẳn nhịp hỏi thăm, và
 * đó là lúc người dùng đang nhìn màn hình chờ một câu trả lời.
 */

import { BOIDE_ACCOUNT_API_URL } from "@/lib/boide-api-url";

/**
 * 🔴 Hỏi `account-be`, KHÔNG hỏi `ai-be`: trang admin (nơi bật bảo trì) chạy ở
 * `account-be`, và màn bảo trì phải còn hiện được đúng lúc `ai-be` sập — hỏi chính cái
 * máy đang chết thì chỉ ra lỗi mạng, không ra lời nhắn của admin. Hai service đọc CÙNG
 * bảng `maintenance_config`. Chưa đặt `VITE_BOIDE_ACCOUNT_API_URL` thì rơi về
 * `BOIDE_API_URL` (xem `boide-api-url.ts`).
 */
const MAINTENANCE_URL = `${BOIDE_ACCOUNT_API_URL}/v1/maintenance`;

export type MaintenanceState = {
  enabled: boolean;
  message: string;
  eta: string;
  startedAt: string | null;
};

const OFF: MaintenanceState = { enabled: false, message: "", eta: "", startedAt: null };

/**
 * Đè bằng biến môi trường — vẫn giữ để còn treo được bảng bảo trì khi chính backend
 * đã chết hẳn (lúc ấy `GET /v1/maintenance` cũng không trả lời được).
 */
function envForced(): boolean {
  try {
    const raw =
      (typeof import.meta !== "undefined" && import.meta.env
        ? import.meta.env.VITE_MAINTENANCE_MODE
        : undefined) ?? undefined;
    return (
      String(raw ?? "")
        .trim()
        .toLowerCase() === "true"
    );
  } catch {
    return false;
  }
}

let current: MaintenanceState = envForced() ? { ...OFF, enabled: true } : OFF;

const listeners = new Set<() => void>();

function setState(next: MaintenanceState) {
  if (
    next.enabled === current.enabled &&
    next.message === current.message &&
    next.eta === current.eta &&
    next.startedAt === current.startedAt
  ) {
    return;
  }
  current = next;
  for (const listener of listeners) listener();
}

export function subscribeMaintenance(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getMaintenance(): MaintenanceState {
  return current;
}

let inFlight: Promise<MaintenanceState> | null = null;

/**
 * Hỏi máy chủ trạng thái bảo trì.
 *
 * ⚠️ **Nuốt mọi lỗi và GIỮ NGUYÊN trạng thái đang có.** Mất mạng không phải là bảo
 * trì — bật màn bảo trì vì một lượt fetch hỏng là nói dối người dùng về nguyên nhân,
 * và họ sẽ ngồi chờ một thứ không bao giờ xảy ra thay vì kiểm tra lại wifi.
 *
 * Endpoint này KHÔNG bị chính chế độ bảo trì chặn (xem danh sách tha ở backend) và
 * không cần auth — đó là điều kiện để nó phân biệt được "đang bảo trì" với "mất mạng".
 */
export function loadMaintenance(): Promise<MaintenanceState> {
  if (envForced()) return Promise.resolve(current);
  if (inFlight) return inFlight;
  inFlight = fetch(MAINTENANCE_URL)
    .then((res) => (res.ok ? res.json() : null))
    .then((json) => {
      // Khuôn response chung của BE: { data: {...}, status: "success" }.
      const data = json?.data;
      if (data && typeof data.enabled === "boolean") {
        setState({
          enabled: data.enabled,
          message: typeof data.message === "string" ? data.message : "",
          eta: typeof data.eta === "string" ? data.eta : "",
          startedAt: typeof data.started_at === "string" ? data.started_at : null,
        });
      }
      return current;
    })
    .catch(() => current)
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

let lastProbe = 0;

/**
 * Gọi từ chỗ xử lý response chung khi một lượt gọi API trả về 503.
 *
 * ⚠️ **Chặn nhịp (10 giây).** Một trang có thể bắn chục lượt gọi song song và tất cả
 * cùng nhận 503 — không chặn thì mỗi lần bảo trì bắt đầu là một loạt request dồn
 * thêm vào đúng máy chủ đang quá tải.
 *
 * ⚠️ **KHÔNG tự bật cờ, mà đi HỎI LẠI.** 503 còn đến từ nginx/Cloudflare lúc backend
 * quá tải chứ không phải bảo trì; hai thứ ấy cần hai màn hình khác nhau, và chỉ
 * `/v1/maintenance` mới phân biệt được.
 */
export function noteMaintenanceResponse(status: number): void {
  if (status !== 503) return;
  const now = Date.now();
  if (now - lastProbe < 10_000) return;
  lastProbe = now;
  void loadMaintenance();
}
